/**
 * Decoder for formats the browser handles natively (JPEG, WebP, GIF, BMP,
 * AVIF, and PNG fallback). Embedded ICC profiles are read directly: known
 * profiles are decoded without colour conversion (exact code values) and the
 * profile drives the automatic pipeline settings; unknown profiles are
 * converted to sRGB by the browser's colour management.
 */
import type { EncodingHint, RawFrame } from '../../core/types';
import { defaultHint, makeFrame } from '../frame';
import { iccToHint, parseIcc } from '../icc';
import { cicpPrimaries, cicpTransfer } from './png';

/** Concatenate ICC_PROFILE APP2 segments of a JPEG. */
export function jpegIcc(b: Uint8Array): Uint8Array | null {
  const parts: { seq: number; data: Uint8Array }[] = [];
  let o = 2;
  while (o + 4 < b.length) {
    if (b[o] !== 0xff) break;
    // Fill bytes: any number of 0xFF may precede a marker.
    if (b[o + 1] === 0xff) {
      o++;
      continue;
    }
    const marker = b[o + 1];
    if (marker === 0xda || marker === 0xd9) break;
    const len = (b[o + 2] << 8) | b[o + 3];
    if (marker === 0xe2 && String.fromCharCode(...b.subarray(o + 4, o + 15)) === 'ICC_PROFILE') {
      parts.push({ seq: b[o + 16], data: b.subarray(o + 18, o + 2 + len) });
    }
    o += 2 + len;
  }
  if (!parts.length) return null;
  parts.sort((a, c) => a.seq - c.seq);
  const total = parts.reduce((s, p) => s + p.data.length, 0);
  const out = new Uint8Array(total);
  let k = 0;
  for (const p of parts) {
    out.set(p.data, k);
    k += p.data.length;
  }
  return out;
}

/** ICC profile from a WebP 'ICCP' chunk. */
export function webpIcc(b: Uint8Array): Uint8Array | null {
  let o = 12;
  while (o + 8 <= b.length) {
    const id = String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]);
    const len = b[o + 4] | (b[o + 5] << 8) | (b[o + 6] << 16) | (b[o + 7] << 24);
    if (id === 'ICCP') return b.subarray(o + 8, o + 8 + len);
    o += 8 + len + (len & 1);
  }
  return null;
}

/** AVIF/HEIF 'colr' box: nclx code points or an embedded ICC profile. */
export function avifColr(b: Uint8Array): { nclx?: { p: number; t: number }; icc?: Uint8Array } | null {
  const limit = Math.min(b.length - 12, 1 << 20);
  for (let o = 4; o < limit; o++) {
    if (b[o] === 0x63 && b[o + 1] === 0x6f && b[o + 2] === 0x6c && b[o + 3] === 0x72) {
      const size = (b[o - 4] << 24) | (b[o - 3] << 16) | (b[o - 2] << 8) | b[o - 1];
      const type = String.fromCharCode(b[o + 4], b[o + 5], b[o + 6], b[o + 7]);
      if (type === 'nclx') return { nclx: { p: (b[o + 8] << 8) | b[o + 9], t: (b[o + 10] << 8) | b[o + 11] } };
      if (type === 'prof' || type === 'rICC') return { icc: b.subarray(o + 8, o - 4 + size) };
    }
  }
  return null;
}

interface BrowserHint {
  hint: EncodingHint;
  /** Let the browser convert to sRGB (profile present but not understood). */
  convert: boolean;
  profileName?: string;
}

function hintFor(format: string, b: Uint8Array): BrowserHint {
  let icc: Uint8Array | null = null;
  if (format === 'jpeg') icc = jpegIcc(b);
  else if (format === 'webp') icc = webpIcc(b);
  else if (format === 'avif') {
    const c = avifColr(b);
    if (c?.nclx) {
      const p = cicpPrimaries(c.nclx.p);
      const t = cicpTransfer(c.nclx.t);
      if (p && t && t !== 'pq' && t !== 'hlg')
        return {
          hint: { transfer: t, primaries: p, source: 'format', description: `AVIF nclx (primaries ${c.nclx.p}, transfer ${c.nclx.t})` },
          convert: false,
        };
      if (t === 'pq' || t === 'hlg')
        return {
          hint: { ...defaultHint('HDR AVIF tone-mapped to SDR sRGB by the browser (canvas decoding is 8-bit)'), source: 'format' },
          convert: true,
        };
    }
    icc = c?.icc ?? null;
  }
  if (icc) {
    const info = parseIcc(icc);
    if (info && info.trc && info.primaries !== undefined) {
      return { hint: iccToHint(info), convert: false, profileName: info.description };
    }
    return {
      hint: { ...defaultHint('Embedded ICC profile not understood: converted to sRGB by browser colour management'), source: 'icc' },
      convert: true,
      profileName: info?.description,
    };
  }
  return { hint: defaultHint('No embedded colour profile: assuming sRGB'), convert: false };
}

/** Decode with createImageBitmap + canvas (works in windows and workers). */
export async function decodeBrowser(bytes: Uint8Array, name: string, format: string): Promise<RawFrame> {
  const { hint, convert, profileName } = hintFor(format, bytes);
  const mime: Record<string, string> = {
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    gif: 'image/gif',
    bmp: 'image/bmp',
    avif: 'image/avif',
    png: 'image/png',
  };
  const blob = new Blob([bytes as BlobPart], { type: mime[format] ?? '' });
  const bmp = await createImageBitmap(blob, {
    colorSpaceConversion: convert ? 'default' : 'none',
    premultiplyAlpha: 'none',
    imageOrientation: 'from-image',
  });
  const { width, height } = bmp;
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' }) as OffscreenCanvasRenderingContext2D;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const rgba = ctx.getImageData(0, 0, width, height, { colorSpace: 'srgb' }).data;
  const warnings: string[] = [];
  if (format === 'jpeg' || format === 'webp' || format === 'avif')
    warnings.push(`${format.toUpperCase()} is lossy: compression artefacts and chroma subsampling affect pixel-level measurements.`);
  if (format === 'gif') warnings.push('GIF is limited to a 256-colour palette; only the first animation frame is loaded.');
  if (format === 'avif') warnings.push('AVIF is decoded through the browser at 8 bits per channel.');
  const info: Record<string, string> = { Decoder: 'browser (createImageBitmap)' };
  if (profileName) info['ICC profile'] = profileName;
  return makeFrame({
    width,
    height,
    data: new Uint8Array(rgba.buffer, rgba.byteOffset, rgba.byteLength),
    channels: 4,
    maxCode: 255,
    bitDepth: 8,
    format,
    name,
    encoding: hint,
    info,
    warnings,
  });
}
